import { clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';
import { useEffect, useRef } from 'react';

import CloseIcon from '~/assets/icons/Close';
import useClickOutside from '~/hooks/useClickOutside';
import type { ModalFrameProps } from '~/legacy/types';
import {
  bem,
  getAppRoot,
  modalFramePosition,
  modalFrameSemantic
} from '~/legacy/utils';

// How the frame was dismissed, handed to onCloseClick as its SECOND argument so a caller that
// needs to tell the three apart (the search panel records which control closed it) can. The first
// argument stays what it always was - the click on the backdrop path, nothing on the other two -
// because callers such as useModal's closer treat a truthy first argument as a DOM event and call
// preventDefault on it.
export type ModalCloseReason = 'backdrop' | 'close_button' | 'escape_key';

const ModalFrame = ({
  onCloseClick,
  children,
  centered,
  setBorderRadius,
  disableClickOutside,
  hideClose,
  isFullBody,
  background = modalFrameSemantic.WHITE,
  isFullWidth,
  positionSemantic = modalFramePosition.DEFAULT,
  showBorder
}: ModalFrameProps) => {
  const ref = useRef(null);
  const scrollTargetElement = getAppRoot();
  useClickOutside(
    ref,
    (event?: MouseEvent) => onCloseClick(event, 'backdrop'),
    disableClickOutside
  );

  useEffect(() => {
    const handler = ({ keyCode }: { keyCode: number }) => {
      if (keyCode === 27) {
        onCloseClick(undefined, 'escape_key');
      }
    };
    document.addEventListener('keyup', handler);

    return () => document.removeEventListener('keyup', handler);
  });

  const onCloseOverlay = () => {
    onCloseClick(undefined, 'close_button');
    clearAllBodyScrollLocks();
    scrollTargetElement && enableBodyScroll(scrollTargetElement);
  };

  const frameSemantic = [
    centered ? 'centered' : '',
    setBorderRadius ? 'set-border-radius' : '',
    isFullBody ? 'is-full-body' : '',
    isFullWidth ? 'is-full-width' : '',
    background,
    `position-${positionSemantic}`,
    showBorder ? 'show-border' : '',
    'tss-domain'
  ];

  return (
    <div className={bem('modal-frame', frameSemantic)} ref={ref}>
      <div
        className={bem('modal-frame__body', [
          background,
          isFullBody ? 'is-full-body' : ''
        ])}
      >
        <div className={bem('modal-frame__content', { isFullBody })}>
          {!hideClose && (
            <div className="modal-frame__close">
              <button
                type="button"
                aria-label="Close"
                onClick={onCloseOverlay}
                className="modal-frame__close__icon flex size-6 items-center justify-center"
              >
                <CloseIcon width="12" height="12" stroke="black" />
              </button>
            </div>
          )}
          <div className="modal-frame__inner">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default ModalFrame;
